// src/services/menuService.js
import { supabase } from './supabase';

// ============================================
// 分類管理 (Categories)
// ============================================

export const getCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('order', { ascending: true });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getCategoryById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createCategory = async (categoryData) => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .insert([categoryData])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateCategory = async (id, categoryData) => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .update(categoryData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteCategory = async (id) => {
  try {
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================
// 菜式管理 (Menu Items)
// ============================================

export const getMenuItems = async (options = {}) => {
  try {
    let query = supabase
      .from('menu_items')
      .select('*');

    // 過濾選項
    if (options.categoryId) {
      query = query.contains('categories', [options.categoryId]);
    }
    
    if (options.isAvailable !== undefined) {
      query = query.eq('is_available', options.isAvailable);
    }

    if (options.isHidden !== undefined) {
      query = query.eq('is_hidden', options.isHidden);
    }

    // 排序
    if (options.orderBy) {
      query = query.order(options.orderBy, { 
        ascending: options.ascending !== false 
      });
    }

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getMenuItemById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getMenuItemBySlug = async (slug) => {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 獲取菜式及其關聯的分類和修飾項組
export const getMenuItemWithDetails = async (id) => {
  try {
    // 獲取菜式基本資訊
    const itemResult = await getMenuItemById(id);
    if (!itemResult.success) return itemResult;

    const item = itemResult.data;

    // 獲取分類詳情
    let categoryDetails = [];
    if (item.categories && item.categories.length > 0) {
      const { data: categories, error: catError } = await supabase
        .from('categories')
        .select('*')
        .in('id', item.categories);
      
      if (!catError) categoryDetails = categories;
    }

    // 獲取修飾項組詳情（包含選項）
    let modifierGroupDetails = [];
    if (item.modifier_groups && item.modifier_groups.length > 0) {
      const groupsResult = await getModifierGroupsByIds(item.modifier_groups);
      if (groupsResult.success) {
        modifierGroupDetails = groupsResult.data;
      }
    }

    return {
      success: true,
      data: {
        ...item,
        category_details: categoryDetails,
        modifier_group_details: modifierGroupDetails
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createMenuItem = async (itemData) => {
  try {
    // 如果沒有 slug，自動生成
    if (!itemData.slug && itemData.name) {
      itemData.slug = itemData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    }

    const { data, error } = await supabase
      .from('menu_items')
      .insert([itemData])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateMenuItem = async (id, itemData) => {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .update(itemData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteMenuItem = async (id) => {
  try {
    const { error } = await supabase
      .from('menu_items')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 批量更新菜式的供應狀態
export const bulkUpdateAvailability = async (itemIds, isAvailable) => {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .update({ is_available: isAvailable })
      .in('id', itemIds)
      .select();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================
// 修飾項組管理 (Modifier Groups)
// ============================================

export const getModifierGroups = async () => {
  try {
    const { data, error } = await supabase
      .from('modifier_groups')
      .select('*')
      .order('order', { ascending: true, nullsFirst: false })
      .order('name', { ascending: true });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getModifierGroupById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('modifier_groups')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 獲取修飾項組及其所有選項
export const getModifierGroupWithOptions = async (id) => {
  try {
    const groupResult = await getModifierGroupById(id);
    if (!groupResult.success) return groupResult;

    const { data: modifiers, error: modError } = await supabase
      .from('modifiers')
      .select('*')
      .eq('modifier_group_id', id)
      .order('order', { ascending: true });

    if (modError) throw modError;

    return {
      success: true,
      data: {
        ...groupResult.data,
        modifiers: modifiers || []
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 批量獲取多個修飾項組及其選項
export const getModifierGroupsByIds = async (ids) => {
  try {
    const { data: groups, error: groupError } = await supabase
      .from('modifier_groups')
      .select('*')
      .in('id', ids);

    if (groupError) throw groupError;

    // 獲取所有相關的修飾項
    const { data: modifiers, error: modError } = await supabase
      .from('modifiers')
      .select('*')
      .in('modifier_group_id', ids)
      .order('order', { ascending: true });

    if (modError) throw modError;

    // 將修飾項組合到對應的組中
    const groupsWithModifiers = groups.map(group => ({
      ...group,
      modifiers: modifiers.filter(mod => mod.modifier_group_id === group.id)
    }));

    return { success: true, data: groupsWithModifiers };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createModifierGroup = async (groupData) => {
  try {
    const { data, error } = await supabase
      .from('modifier_groups')
      .insert([groupData])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateModifierGroup = async (id, groupData) => {
  try {
    const { data, error } = await supabase
      .from('modifier_groups')
      .update(groupData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteModifierGroup = async (id) => {
  try {
    // 會自動刪除關聯的 modifiers (CASCADE)
    const { error } = await supabase
      .from('modifier_groups')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================
// 修飾項選項管理 (Modifiers)
// ============================================

export const getModifiersByGroupId = async (groupId) => {
  try {
    const { data, error } = await supabase
      .from('modifiers')
      .select('*')
      .eq('modifier_group_id', groupId)
      .order('order', { ascending: true });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createModifier = async (modifierData) => {
  try {
    const { data, error } = await supabase
      .from('modifiers')
      .insert([modifierData])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateModifier = async (id, modifierData) => {
  try {
    const { data, error } = await supabase
      .from('modifiers')
      .update(modifierData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteModifier = async (id) => {
  try {
    const { error } = await supabase
      .from('modifiers')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 批量創建修飾項
export const bulkCreateModifiers = async (modifiers) => {
  try {
    const { data, error } = await supabase
      .from('modifiers')
      .insert(modifiers)
      .select();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================
// 統計與報表
// ============================================

export const getMenuStatistics = async () => {
  try {
    // 獲取分類數量
    const { count: categoryCount, error: catError } = await supabase
      .from('categories')
      .select('*', { count: 'exact', head: true });

    // 獲取菜式數量
    const { count: itemCount, error: itemError } = await supabase
      .from('menu_items')
      .select('*', { count: 'exact', head: true });

    // 獲取可用菜式數量
    const { count: availableCount, error: availError } = await supabase
      .from('menu_items')
      .select('*', { count: 'exact', head: true })
      .eq('is_available', true);

    // 獲取修飾項組數量
    const { count: modifierGroupCount, error: modGroupError } = await supabase
      .from('modifier_groups')
      .select('*', { count: 'exact', head: true });

    if (catError || itemError || availError || modGroupError) {
      throw new Error('Error fetching statistics');
    }

    return {
      success: true,
      data: {
        totalCategories: categoryCount || 0,
        totalItems: itemCount || 0,
        availableItems: availableCount || 0,
        totalModifierGroups: modifierGroupCount || 0
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 搜尋菜式
export const searchMenuItems = async (searchTerm) => {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .select('*')
      .or(`name.ilike.%${searchTerm}%,excerpt.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};